import { Injectable } from '@angular/core';
import { ShepherdService } from 'angular-shepherd';
import { HomeClient } from './api.service';
import { Observable, of, tap } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { TourCompletionKey } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class TourService {
  constructor(
    private readonly shepherd: ShepherdService,
    private readonly homeClient: HomeClient,
    private readonly localStorageService: LocalStorageService,
  ) {}

  determineTourRequiredStatus(): Observable<boolean> {
    const educatorRequiresTour = this.localStorageService.get(TourCompletionKey.EducatorRequiresTour);

    if (educatorRequiresTour != null) {
      if (educatorRequiresTour === 'true') {
        return of(true);
      } else {
        return of(false);
      }
    }

    return this.homeClient.getEducatorRequiresTour().pipe(
      tap((tourRequired) => {
        this.localStorageService.set(TourCompletionKey.EducatorRequiresTour, tourRequired.toString());
        this.localStorageService.set(TourCompletionKey.HomeTourComplete, 'false');
        this.localStorageService.set(TourCompletionKey.ToDoTourComplete, 'false');
        this.localStorageService.set(TourCompletionKey.AppSummaryTourComplete, 'false');
      }),
    );
  }

  cleanupTourLocalStorage() {
    this.localStorageService.remove(TourCompletionKey.EducatorRequiresTour);
    this.localStorageService.remove(TourCompletionKey.HomeTourComplete);
    this.localStorageService.remove(TourCompletionKey.ToDoTourComplete);
    this.localStorageService.remove(TourCompletionKey.AppSummaryTourComplete);
  }

  hasTourBeenCompleted(tour: TourType) {
    switch (tour) {
      case TourType.Home:
        const isHomeCompleted = this.localStorageService.get(TourCompletionKey.HomeTourComplete);
        return isHomeCompleted === 'true';
      case TourType.ToDo:
        const isToDoCompleted = this.localStorageService.get(TourCompletionKey.ToDoTourComplete);
        return isToDoCompleted === 'true';
      case TourType.ApplicationSummary:
        const isAppSummaryCompleted = this.localStorageService.get(TourCompletionKey.AppSummaryTourComplete);
        return isAppSummaryCompleted === 'true';
      default:
        return false;
    }
  }

  buttons = {
    dismiss: {
      classes: 'btn btn-outline-danger',
      text: 'Dismiss',
      type: 'cancel',
    },
    start: {
      classes: 'btn btn-outline-primary',
      text: 'Begin Tour',
      type: 'next',
    },
    next: {
      classes: 'btn btn-outline-primary',
      text: 'Next',
      type: 'next',
    },
    back: {
      classes: 'btn btn-outline-secondary',
      secondary: true,
      text: 'Back',
      type: 'back',
    },
    complete: {
      classes: 'btn btn-outline-primary',
      text: 'Finish Tour',
      type: 'next',
    },
    completeHome: {
      classes: 'btn btn-outline-primary',
      text: 'Finish Tour',
      action: () => {
        this.localStorageService.set(TourCompletionKey.HomeTourComplete, 'true');
        this.shepherd.complete();
      },
    },
    completeToDo: {
      classes: 'btn btn-outline-primary',
      text: 'Finish Tour',
      action: () => {
        this.localStorageService.set(TourCompletionKey.ToDoTourComplete, 'true');
        this.shepherd.complete();
      },
    },
    completeAppSummary: {
      classes: 'btn btn-outline-primary',
      text: 'Finish Tour',
      action: () => {
        this.localStorageService.set(TourCompletionKey.AppSummaryTourComplete, 'true');
        this.shepherd.complete();
      },
    },
  };

  startHomeTour() {
    this.shepherd.addSteps([
      {
        id: 'home-opener',
        buttons: [this.buttons.dismiss, this.buttons.start],
        scrollTo: false,
        title: 'Educator Portal',
        text: `Welcome to the educator portal of the Arizona Department of Education's new certification system.
          Let's take a quick tour.`,
      },
      {
        id: 'to-do-tasks',
        attachTo: {
          element: '#to-do-tasks-tour',
          on: 'bottom',
        },
        buttons: [this.buttons.back, this.buttons.next],
        scrollTo: true,
        title: 'To-Do Tasks',
        text: 'This area lists time-sensitive items ready for you to take action on.',
      },
      {
        id: 'notifications',
        attachTo: {
          element: '#notifications-tour',
          on: 'bottom',
        },
        buttons: [this.buttons.back, this.buttons.next],
        scrollTo: false,
        title: 'Notifications',
        text: 'This area lists new notifications from the ADE often regarding your application.',
      },
      {
        id: 'applications',
        attachTo: {
          element: '#applications-tour',
          on: 'top',
        },
        buttons: [this.buttons.back, this.buttons.completeHome],
        scrollTo: false,
        title: 'My Applications',
        text: `This area provides insight into the status of a current application's individual service items as well as previous applications.`,
      },
    ]);

    this.shepherd.start();
  }

  startToDoTour() {
    this.shepherd.addSteps([
      {
        id: 'todo-opener',
        buttons: [this.buttons.completeToDo],
        scrollTo: false,
        title: 'To-Do Tasks',
        text: `This page exists to list individual items requiring your attention. Items listed are time-sensitive,
        and may include things such as certificate renewals, deficiency removals, required forms, or upcoming fingerprint deadlines.`,
      },
    ]);

    this.shepherd.start();
  }

  startApplicationSummaryTour() {
    this.shepherd.addSteps([
      {
        id: 'app-summary-opener',
        buttons: [this.buttons.dismiss, this.buttons.next],
        scrollTo: false,
        title: 'Application Summary',
        text: `This page is the heart of the application process. It allows you to start a new application by adding one-or-more service items, and
        summarizes your pending application as it progresses.`,
      },
      {
        id: 'add-certificate',
        attachTo: {
          element: '#add-certificate-tour',
          on: 'top',
        },
        buttons: [this.buttons.back, this.buttons.completeAppSummary],
        scrollTo: true,
        title: 'Adding a Service Item',
        text: 'You can add a service by clicking on the corresponding card. Some services are available to you, and those you are not eligible for are disabled.',
      },
    ]);

    this.shepherd.start();
  }
}

export enum TourType {
  Home,
  ToDo,
  ApplicationSummary,
}
